import React, { useEffect, useState, useRef } from 'react';
import * as XLSX from 'xlsx';
import { MESSAGE_TYPE, useFloatMessage } from '../../../Providers/FloatMessage';
import { SecondaryBase } from '../../Commons/SecondaryBase';
import { Confirm } from '../../../Commons/Modals/Confirm';
import {
    GetAdminParticipants,
    ToggleVisibility,
    SelectWinner,
    UpdatePosition,
    UpdateBannerMode,
    GetSettings,
    ToggleActiveTrivia,
    UpdateOverlayVisibility,
    GetAdminStats,
    GetQuestions,
    ResetQuizzData
} from '../../../../utilities/BL/OverlayQuizz';

const formatMexicoDate = (dateString) => {
    if (!dateString) return 'No disponible';
    const date = new Date(dateString);
    return date.toLocaleString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
    }) + ' hrs';
};

function throttle(func, limit) {
    let inThrottle;
    return function(...args) {
        if (!inThrottle) {
            func.apply(this, args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    }
}

export function OverlayQuizzBoard({ getOutOfTheDynamic }) {
    const { showMessage } = useFloatMessage();

    const [participants, setParticipants] = useState([]);
    const [stats, setStats] = useState({
        qr_scan: 0,
        page_view: 0,
        form_start: 0,
        form_complete: 0,
        quiz_start: 0,
        quiz_complete: 0,
        whatsapp_click: 0,
        whatsapp_click_unique: 0
    });
    const [isLoading, setIsLoading] = useState(false);
    const [questions, setQuestions] = useState([]);
    
    // Live Overlay State
    const [offsetY, setOffsetY] = useState(0);
    const [bannerMode, setBannerMode] = useState('invitation'); // invitation, winner
    const [winnerName, setWinnerName] = useState('');
    const [isTriviaActive, setIsTriviaActive] = useState(true);
    const [isOverlayVisible, setIsOverlayVisible] = useState(false);

    // Advanced Panel & Leyenda
    const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    // Search and filters
    const [searchTerm, setSearchTerm] = useState('');
    const [scoreFilter, setScoreFilter] = useState('all');
    const [winnerFilter, setWinnerFilter] = useState('all');

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(25);

    // Confirm Modal States
    const [showConfirm, setShowConfirm] = useState(false);
    const [confirmConfig, setConfirmConfig] = useState({
        message: '',
        action: null,
        textYes: 'Sí, confirmar',
        textNo: 'Cancelar'
    });
    const [confirmData, setConfirmData] = useState(null);

    const throttledUpdatePosition = useRef(
        throttle((val) => {
            UpdatePosition(val);
        }, 80)
    ).current;

    const loadInitialData = async () => {
        setIsLoading(true);
        try {
            const res = await GetAdminParticipants();
            if (res.success && res.data) {
                setParticipants(res.data);
            } else {
                throw new Error(res.message);
            }

            const statsRes = await GetAdminStats();
            if (statsRes.success && statsRes.data) {
                setStats(statsRes.data);
            }

            const questionsRes = await GetQuestions();
            if (questionsRes.success && questionsRes.data) {
                setQuestions(questionsRes.data);
            }

            const settingsRes = await GetSettings();
            if (settingsRes.success && settingsRes.data) {
                setOffsetY(settingsRes.data.offsetY);
                setBannerMode(settingsRes.data.bannerMode);
                setWinnerName(settingsRes.data.winnerName);
                setIsTriviaActive(settingsRes.data.isTriviaActive ?? true);
                setIsOverlayVisible(settingsRes.data.isOverlayVisible ?? false);
            }

            setLastUpdated(new Date().toLocaleString('es-MX', { hour12: false }) + ' hrs');
        } catch (error) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: error.message || 'Error al cargar la información del panel.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadInitialData();
    }, []);

    const triggerSelectWinner = (id, name) => {
        setConfirmConfig({
            message: id === 0 
                ? '¿Estás seguro de que deseas desmarcar al ganador actual de la trivia?' 
                : `¿Estás seguro de que deseas elegir a "${name}" como el ganador oficial de la trivia?`,
            textYes: id === 0 ? 'Sí, desmarcar' : 'Sí, elegir ganador',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: id === 0 ? 'Desmarcando ganador...' : 'Estableciendo ganador...' });
                const res = await SelectWinner(id);
                if (res.success) {
                    setParticipants(prev => 
                        prev.map(p => ({ ...p, isWinner: p.id === id }))
                    );
                    setWinnerName(id === 0 ? '' : name);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: id === 0 ? 'Ganador desmarcado correctamente.' : `¡Felicidades! ${name} ha sido elegido como ganador.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerResetQuizz = () => {
        setConfirmConfig({
            message: '¿Estás segur@ de que deseas reiniciar la dinámica por completo? Esto eliminará de forma irreversible a todos los participantes registrados y las estadísticas en el servidor.',
            textYes: 'Sí, reiniciar todo',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Reiniciando dinámica...' });
                const res = await ResetQuizzData();
                if (res.success) {
                    setParticipants([]);
                    setWinnerName('');
                    setIsOverlayVisible(false);
                    setIsTriviaActive(true);
                    setBannerMode('invitation');
                    setOffsetY(0);
                    setStats({
                        qr_scan: 0,
                        page_view: 0,
                        form_start: 0,
                        form_complete: 0,
                        quiz_start: 0,
                        quiz_complete: 0,
                        whatsapp_click: 0,
                        whatsapp_click_unique: 0
                    });
                    setLastUpdated(new Date().toLocaleString('es-MX', { hour12: false }) + ' hrs');
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: '¡La dinámica ha sido reiniciada por completo!'
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const handlePositionChange = (e) => {
        const val = parseInt(e.target.value, 10) || 0;
        setOffsetY(val);
        throttledUpdatePosition(val);
    };

    const triggerBannerModeChange = (mode) => {
        const activeWinner = participants.find(p => p.isWinner) || winnerName;
        if (mode === 'winner' && !activeWinner) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Aún no has seleccionado un ganador. Establece primero al participante ganador en el listado.'
            });
            return;
        }

        setConfirmConfig({
            message: `¿Estás segur@ de que deseas mostrar ${mode === 'invitation' ? 'los códigos QR para que escaneen' : 'al ganador de la trivia'}?`,
            textYes: 'Sí, cambiar',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando modo del banner...' });
                const res = await UpdateBannerMode(mode);
                if (res.success) {
                    setBannerMode(mode);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: 'Modo de banner cambiado con éxito.'
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerToggleActiveTrivia = (nextState) => {
        setConfirmConfig({
            message: `¿Estás seguro de que deseas ${nextState ? 'ABRIR' : 'CERRAR'} la participación en la trivia?`,
            textYes: nextState ? 'Sí, abrir' : 'Sí, cerrar',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando estado de la trivia...' });
                const res = await ToggleActiveTrivia(nextState);
                if (res.success) {
                    setIsTriviaActive(nextState);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `Trivia ${nextState ? 'abierta (recibiendo registros)' : 'cerrada (bloqueada)'} con éxito.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const triggerToggleOverlayVisibility = (nextState) => {
        setConfirmConfig({
            message: `¿Estás segur@ de que deseas ${nextState ? 'mostrar' : 'ocultar'} el banner de la pantalla?`,
            textYes: nextState ? 'Sí, mostrar' : 'Sí, ocultar',
            textNo: 'Cancelar',
            action: async () => {
                showMessage({ type: MESSAGE_TYPE.INFO, text: 'Actualizando visibilidad del overlay...' });
                const res = await UpdateOverlayVisibility(nextState);
                if (res.success) {
                    setIsOverlayVisible(nextState);
                    showMessage({
                        type: MESSAGE_TYPE.SUCCESS,
                        text: `La marquesina ahora está ${nextState ? 'visible' : 'oculta'} en pantalla.`
                    });
                    return { success: true };
                } else {
                    showMessage({ type: MESSAGE_TYPE.BAD, text: res.message });
                    return { success: false, message: res.message };
                }
            }
        });
        setShowConfirm(true);
    };

    const downloadExcelReport = () => {
        if (participants.length === 0) {
            showMessage({ type: MESSAGE_TYPE.INFO, text: 'No hay registros para exportar.' });
            return;
        }

        showMessage({ type: MESSAGE_TYPE.INFO, text: 'Generando reporte de excel...' });

        // Sort chronologically ascending by registration date
        const sortedParticipants = [...participants].sort((a, b) => new Date(a.createdAtUtc) - new Date(b.createdAtUtc));

        const dataToExport = sortedParticipants.map((p) => {
            const row = {
                'Fecha de registro': formatMexicoDate(p.createdAtUtc),
                Nombre: p.name,
                Correo: p.email,
                'Teléfono': p.phone,
                '¿Es ganador(a)?': p.isWinner ? 'Sí' : '',
                '# de aciertos': `${p.score} de ${questions.length}`,
            };

            let chosenOptionIds = [];
            try {
                if (p.selectedOptionIdsJson) {
                    chosenOptionIds = JSON.parse(p.selectedOptionIdsJson);
                }
            } catch (err) {
                console.error("Error parsing option IDs", p.id, err);
            }

            questions.forEach((q, qIdx) => {
                const questionKey = `Pregunta #${qIdx + 1}: ${q.instruction}`;
                const isText = q.type === 'TEXT' || q.type === 'text' || q.Type === 'TEXT' || q.Type === 'text';
                if (isText) {
                    row[questionKey] = p.feedback ? p.feedback : 'Sin comentarios';
                } else {
                    const options = q.options || [];
                    const chosenOption = options.find(opt => chosenOptionIds.includes(opt.id));
                    row[questionKey] = chosenOption ? chosenOption.text : 'No contestada';
                }
            });

            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToExport);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Participantes');

        const dateStr = new Date().toISOString().slice(0, 10);
        XLSX.writeFile(workbook, `Ranking_Encuesta_B9_${dateStr}.xlsx`);

        showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Excel generado y descargado correctamente.' });
    };

    const handleReturn = () => {
        if (getOutOfTheDynamic) {
            getOutOfTheDynamic();
        } else {
            window.location.href = '/admin/dashboard';
        }
    };

    // Client-side filtering logic
    const filteredParticipants = participants.filter(p => {
        const matchesSearch = !searchTerm || 
            p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            p.phone.includes(searchTerm);
            
        const matchesScore = scoreFilter === 'all' || p.score.toString() === scoreFilter;
        
        const matchesWinner = winnerFilter === 'all' || 
            (winnerFilter === 'winner' && p.isWinner) ||
            (winnerFilter === 'non-winner' && !p.isWinner);

        return matchesSearch && matchesScore && matchesWinner;
    });

    // Pagination logic
    const totalRegistries = filteredParticipants.length;
    const totalPages = Math.ceil(totalRegistries / pageSize) || 1;
    const verifiedPage = Math.min(currentPage, totalPages);
    const startIndex = (verifiedPage - 1) * pageSize;
    const paginatedParticipants = filteredParticipants.slice(startIndex, startIndex + pageSize);

    // Winner details card synchronization
    const winnerDetails = participants.find(p => p.isWinner);
    const winnerNameShow = winnerDetails ? winnerDetails.name : winnerName;

    return (
        <SecondaryBase actionToExecute={handleReturn}>
            {showConfirm && (
                <Confirm 
                    message={confirmConfig.message}
                    actionToYesButton={confirmConfig.action}
                    textYesButton={confirmConfig.textYes}
                    textNoButton={confirmConfig.textNo}
                    setShowModal={setShowConfirm}
                    setConfirmData={setConfirmData}
                />
            )}

            <div className="container-fluid px-4 py-3 OverlayQuizzBoard">
                
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-3">
                    <div>
                        <h1 className="h3 fw-bold text-dark mb-1">
                            <i className="bi bi-house-door-fill text-danger me-2"></i> 
                            Encuesta B9
                        </h1>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-12">
                        <div className="bg-light p-3 rounded-3 border">
                            <p className='fs-3 fw-bold text-center mb-2'>Configuración básica</p>

                            <div className="mb-4">
                                <p className='text-center fs-5'>
                                    Estatus: <span className={`fw-bold ${isOverlayVisible ? 'text-success' : 'text-danger'}`}>{isOverlayVisible ? 'El banner se está mostrando en pantalla' : 'El banner está oculto'}</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => triggerToggleOverlayVisibility(!isOverlayVisible)}
                                    className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                        isOverlayVisible 
                                        ? 'btn-outline-danger' 
                                        : 'btn-outline-success'
                                    }`}
                                >
                                    <i className={`bi ${isOverlayVisible ? 'bi-eye-slash-fill' : 'bi-eye-fill'}`}></i>
                                    {isOverlayVisible ? 'Ocultar banner' : 'Mostrar banner'}
                                </button>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-semibold text-dark text-center small d-block mb-2">
                                    Elige lo que deseas mostrar en el banner
                                </label>
                                <div className="btn-group w-100" role="group">
                                    <button 
                                        type="button" 
                                        onClick={() => triggerBannerModeChange('invitation')}
                                        className={`btn py-2.5 fw-bold ${bannerMode === 'invitation' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    >
                                        {bannerMode === 'invitation' && (
                                            <i className="bi bi-check-circle-fill me-2"></i>
                                        )}
                                        <i className="bi bi-qr-code me-2"></i>
                                        Mostrar los códigos QR para escanear
                                    </button>
                                    <button 
                                        type="button" 
                                        onClick={() => triggerBannerModeChange('winner')}
                                        className={`btn py-2.5 fw-bold ${bannerMode === 'winner' ? 'btn-danger' : 'btn-outline-danger'}`}
                                    >
                                        {bannerMode === 'winner' && (
                                            <i className="bi bi-check-circle-fill me-2"></i>
                                        )}
                                        <i className="bi bi-trophy-fill me-2"></i>
                                        Mostrar al ganador(a)
                                    </button>
                                </div>
                            </div>
                            
                            <span className="d-block text-center text-dark small fw-bold mb-2">Vista previa en pantalla:</span>
                            <div className="bg-black text-white p-3 rounded-2 text-center small mb-3" style={{ minHeight: '65px' }}>
                                {bannerMode === 'invitation' ? (
                                    <div className="d-flex justify-content-around align-items-center">
                                        <i className="bi bi-qr-code"></i>
                                        <span className="fw-semibold">Escanea para participar en la Encuesta B9</span>
                                        <i className="bi bi-qr-code"></i>
                                    </div>
                                ) : (
                                    <div>
                                        <span className="d-block text-warning fw-bold text-uppercase" style={{ fontSize: '0.65rem' }}>🎉 Ganador 🎉</span>
                                        <span className="fw-bold fs-6">{winnerNameShow || '(Ninguno seleccionado)'}</span>
                                    </div>
                                )}
                            </div>

                            {/* Tarjeta del Ganador actual (Top Card Sync) */}
                            <div className={`card p-3 border-0 rounded-3 shadow-sm ${winnerNameShow ? 'bg-warning-subtle text-warning-emphasis' : 'bg-light text-secondary'} text-center mb-1`}>
                                <div className="d-flex justify-content-center align-items-center flex-wrap gap-2">
                                    <i className={`bi ${winnerNameShow ? 'bi-trophy-fill fs-4' : 'bi-exclamation-triangle-fill fs-5'}`}></i>
                                    {winnerDetails ? (
                                        <span className="fw-bold">
                                            Ganador seleccionado: {winnerDetails.name} ({winnerDetails.email} / {winnerDetails.phone}) — {winnerDetails.score}/4 aciertos
                                        </span>
                                    ) : winnerNameShow ? (
                                        <span className="fw-bold">Ganador seleccionado: {winnerNameShow}</span>
                                    ) : (
                                        <span>No se ha seleccionado ningún ganador todavía. Selecciona un participante del ranking.</span>
                                    )}

                                    {winnerNameShow && (
                                        <button 
                                            type="button" 
                                            className="btn btn-sm btn-danger fw-bold ms-2 py-1 px-3"
                                            onClick={() => triggerSelectWinner(0, '')}
                                        >
                                            <i className="bi bi-x-circle-fill me-1"></i> Desmarcar ganador
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <p className='fs-4 text-center mt-5 mb-1 fw-bold'>Estadísticas en Tiempo Real</p>
                <div className="text-center mb-3">
                    <button 
                        type="button" 
                        className="btn btn-outline-primary mb-1"
                        onClick={loadInitialData}
                        disabled={isLoading}
                    >
                        <i className="bi bi-arrow-clockwise me-1"></i> Recargar estadísticas y listado
                    </button>
                    {lastUpdated && (
                        <div className="text-muted small mt-1">
                            <i className="bi bi-clock-history me-1"></i> Última actualización: {lastUpdated}
                        </div>
                    )}
                </div>

                <div className="row g-3 mb-4 text-center">
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-qr-code text-danger fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Escaneos QR</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.qr_scan ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-globe text-primary fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Ingresos Página</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.page_view ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-pencil-square text-warning fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Inicios Registro</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.form_start ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-check-circle-fill text-success fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Registros Completos</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.form_complete ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-play-fill text-info fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Inicios Trivia</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.quiz_start ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-6 col-md-4 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-flag-fill text-dark fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Trivias Completadas</div>
                            <div className="fs-3 fw-bold text-dark mt-1">{stats.quiz_complete ?? 0}</div>
                        </div>
                    </div>
                    <div className="col-12 col-md-12 col-xl">
                        <div className="card border-0 shadow-sm rounded-3 p-3 bg-white h-100">
                            <i className="bi bi-whatsapp text-success fs-3 mb-2"></i>
                            <div className="text-secondary small fw-semibold">Clics WhatsApp</div>
                            <div className="fs-3 fw-bold text-dark mt-1">
                                {stats.whatsapp_click_unique ?? 0} únicos ({stats.whatsapp_click ?? 0} total)
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row g-4">
                    <div className="col-12">
                        <div className="card shadow-sm border-0 rounded-3 p-4 bg-white">
                            <div className="d-flex justify-content-between align-items-center mb-3 border-bottom pb-2">
                                <h3 className="h5 fw-bold text-dark mb-0">
                                    <i className="bi bi-list-stars me-2 text-danger"></i>
                                    Listado de Participantes
                                </h3>
                                <span className="px-3 py-1.5 fw-bold bg-light rounded-pill border small">
                                    Total: {totalRegistries} participantes
                                </span>
                            </div>

                            {/* Buscador y Filtros */}
                            <div className="row g-2 mb-3 bg-light p-3 rounded border">
                                <div className="col-12 col-md-6 col-lg-5">
                                    <label className="form-label small fw-semibold text-secondary mb-1">Buscar participante</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><i className="bi bi-search"></i></span>
                                        <input 
                                            type="text" 
                                            className="form-control" 
                                            placeholder="Nombre, correo o teléfono..." 
                                            value={searchTerm}
                                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                        />
                                    </div>
                                </div>
                                <div className="col-6 col-md-3 col-lg-3">
                                    <label className="form-label small fw-semibold text-secondary mb-1">Aciertos</label>
                                    <select 
                                        className="form-select"
                                        value={scoreFilter}
                                        onChange={(e) => { setScoreFilter(e.target.value); setCurrentPage(1); }}
                                    >
                                        <option value="all">Todos</option>
                                        <option value="4">Perfectos (4 / 4)</option>
                                        <option value="3">3 aciertos</option>
                                        <option value="2">2 aciertos</option>
                                        <option value="1">1 acierto</option>
                                        <option value="0">0 aciertos</option>
                                    </select>
                                </div>
                                <div className="col-6 col-md-3 col-lg-4">
                                    <label className="form-label small fw-semibold text-secondary mb-1">Estatus</label>
                                    <select 
                                        className="form-select"
                                        value={winnerFilter}
                                        onChange={(e) => { setWinnerFilter(e.target.value); setCurrentPage(1); }}
                                    >
                                        <option value="all">Todos</option>
                                        <option value="winner">Ganadores</option>
                                        <option value="non-winner">No ganadores</option>
                                    </select>
                                </div>
                            </div>

                            <button 
                                type="button" 
                                className="btn btn-outline-success d-block ms-auto mb-3" 
                                onClick={downloadExcelReport}
                            >
                                <i className="bi bi-file-earmark-excel-fill me-1"></i> 
                                Exportar datos a Excel
                            </button>

                            {isLoading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-danger" role="status">
                                        <span className="visually-hidden">Cargando participantes...</span>
                                    </div>
                                    <p className="text-secondary small mt-2">Cargando base de datos...</p>
                                </div>
                            ) : paginatedParticipants.length > 0 ? (
                                <div>
                                    <div className="table-responsive" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                        <table className="table align-middle table-hover border-secondary-subtle">
                                            <thead className="table-light sticky-top" style={{ zIndex: 10 }}>
                                                <tr className="small text-secondary fw-bold text-uppercase">
                                                    <th scope="col">Nombre</th>
                                                    <th scope="col">Contacto</th>
                                                    <th scope="col" className="text-center">Respuestas Correctas</th>
                                                    <th scope="col">Opinión</th>
                                                    <th scope="col">Fecha Registro</th>
                                                    <th scope="col" className="text-center">Acciones</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {paginatedParticipants.map((p) => {
                                                    return (
                                                        <tr key={p.id} className={p.isWinner ? 'table-warning' : p.isHidden ? 'opacity-50' : ''}>
                                                            <td>
                                                                <div className="fw-bold text-dark">{p.name}</div>
                                                                {p.isWinner && (
                                                                    <span className="badge bg-warning text-dark rounded-pill fw-bold" style={{ fontSize: '0.65rem' }}>🏆 GANADOR</span>
                                                                )}
                                                            </td>
                                                            <td>
                                                                <div className="small text-secondary"><i className="bi bi-envelope me-1"></i>{p.email}</div>
                                                                <div className="small text-secondary"><i className="bi bi-whatsapp text-success me-1"></i>{p.phone}</div>
                                                            </td>
                                                            <td className="text-center fw-bold fs-5 text-danger">
                                                                {p.score} <span className="text-muted fs-6">/ 4</span>
                                                            </td>
                                                            <td className="small" style={{ maxWidth: '240px' }}>
                                                                {p.feedback ? (
                                                                    <span title={p.feedback} className="text-dark d-inline-block text-truncate" style={{ maxWidth: '240px' }}>
                                                                        <i className="bi bi-chat-left-text me-1 text-danger"></i>
                                                                        {p.feedback}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-secondary opacity-50 fst-italic">Sin comentarios</span>
                                                                )}
                                                            </td>
                                                            <td className="small text-muted">
                                                                {formatMexicoDate(p.createdAtUtc)}
                                                            </td>
                                                            <td className="text-center">
                                                                <div className="d-flex justify-content-center gap-2">
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => triggerSelectWinner(p.isWinner ? 0 : p.id, p.name)}
                                                                        className={`btn btn-sm fw-semibold transition-all ${p.isWinner ? 'btn-danger' : 'btn-outline-warning text-dark'}`}
                                                                        title={p.isWinner ? "Desmarcar como ganador" : "Establecer como ganador"}
                                                                    >
                                                                        {p.isWinner ? (
                                                                            <>
                                                                                <i className="bi bi-x-circle-fill me-1"></i> Desmarcar
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <i className="bi bi-trophy-fill me-1"></i> Establecer como ganador
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>

                                    <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top flex-wrap gap-2">
                                        <div className="small text-secondary">
                                            Mostrando {startIndex + 1} - {Math.min(startIndex + pageSize, totalRegistries)} de {totalRegistries} registros
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <select 
                                                className="form-select form-select-sm border-secondary-subtle" 
                                                style={{ width: '80px' }}
                                                value={pageSize}
                                                onChange={(e) => {
                                                    setPageSize(Number(e.target.value));
                                                    setCurrentPage(1);
                                                }}
                                            >
                                                <option value={10}>10</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={100}>100</option>
                                            </select>
                                            
                                            <button 
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === 1}
                                                onClick={() => setCurrentPage(1)}
                                            >
                                                <i className="bi bi-chevron-double-left"></i>
                                            </button>
                                            <button 
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === 1}
                                                onClick={() => setCurrentPage(verifiedPage - 1)}
                                            >
                                                <i className="bi bi-chevron-left"></i>
                                            </button>
                                            <span className="small fw-semibold text-dark px-2">
                                                Página {verifiedPage} de {totalPages}
                                            </span>
                                            <button 
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === totalPages}
                                                onClick={() => setCurrentPage(verifiedPage + 1)}
                                            >
                                                <i className="bi bi-chevron-right"></i>
                                            </button>
                                            <button 
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                disabled={verifiedPage === totalPages}
                                                onClick={() => setCurrentPage(totalPages)}
                                            >
                                                <i className="bi bi-chevron-double-right"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-5 text-secondary">
                                    <p className="fs-5 mb-0">No se encontraron registros de participación.</p>
                                    <p className="small text-muted">Asegúrate de ajustar los filtros o el buscador de texto.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Sección Plegable de Configuración Avanzada */}
                <div className="card border-0 rounded-3 shadow-sm bg-white mt-5">
                    <div 
                        className="card-header bg-light border-0 py-3 d-flex justify-content-between align-items-center cursor-pointer"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                        onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                    >
                        <h3 className="h5 fw-bold mb-0 text-dark">
                            <i className="bi bi-gear-fill me-2 text-danger"></i>
                            Configuración avanzada
                        </h3>
                        <i className={`bi ${isAdvancedOpen ? 'bi-chevron-up' : 'bi-chevron-down'} fs-5`}></i>
                    </div>
                    {isAdvancedOpen && (
                        <div className="card-body p-4 border-top">
                            <div className="mb-4">
                                <label className="form-label fw-semibold text-secondary d-flex justify-content-between small">
                                    <span>Posición Vertical (Offset Y)</span>
                                    <span className="badge bg-secondary">{offsetY}px</span>
                                </label>
                                <input 
                                    type="range" 
                                    className="form-range" 
                                    min="0" 
                                    max="600" 
                                    step="5"
                                    value={offsetY} 
                                    onChange={handlePositionChange} 
                                />
                                <div className="d-flex justify-content-between text-muted" style={{ fontSize: '0.7rem' }}>
                                    <span>Abajo (0px)</span>
                                    <span>Arriba (600px)</span>
                                </div>
                            </div>

                            <div className="mb-4">
                                <label className="form-label fw-semibold text-secondary small d-block mb-2">
                                    Participación en Trivia
                                </label>
                                <button
                                    type="button"
                                    onClick={() => triggerToggleActiveTrivia(!isTriviaActive)}
                                    className={`btn w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 transition-all ${
                                        isTriviaActive 
                                        ? 'btn-outline-secondary' 
                                        : 'btn-outline-danger'
                                    }`}
                                >
                                    <i className={`bi ${isTriviaActive ? 'bi-check-circle-fill' : 'bi-slash-circle-fill'}`}></i>
                                    {isTriviaActive ? 'Trivia activa (Recibiendo registros)' : 'Trivia cerrada (Registro bloqueado)'}
                                </button>
                            </div>

                            <hr className="my-4" />

                            <div className="bg-danger-subtle p-3 rounded border border-danger border-opacity-25">
                                <h4 className="fs-6 fw-bold text-danger mb-2">
                                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                    Zona Peligrosa
                                </h4>
                                <p className="small text-danger-emphasis mb-3">
                                    Reiniciar la dinámica eliminará permanentemente todos los datos de los participantes registrados, métricas, estadísticas e in-memory cache en el servidor de forma irreversible.
                                </p>
                                <button
                                    type="button"
                                    onClick={triggerResetQuizz}
                                    className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2"
                                >
                                    <i className="bi bi-trash-fill"></i>
                                    Reiniciar dinámica por completo
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </SecondaryBase>
    );
}

export default OverlayQuizzBoard;
